import { useQuery } from '@tanstack/react-query'
import { GroupNote } from './GroupNote'
import { NOTE_ISSUES } from '../../../lib/deskGroupNote'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Panel } from '../../../components/Panel'
import { TrendChart } from '../../../components/charts/TrendChart'
import { DeskFrame } from './DeskFrame'
import { NotYetLive } from './NotYetLive'
import { useDeskState } from './useDeskState'
import { Failed, Loading } from './ArticleBits'
import { deskApi, type Compared, type DeskPub, type IssueDetail, type IssueListItem, type IssuesResp, type IssueRow } from '../../../api/visitorsDesk'
import { ApiError } from '../../../api/client'
import { depthBars, issueHref, mmss, pickupSeries, tileCompareText } from '../../../lib/deskIssues'
import { td, th, mono } from '../../../lib/deskArticles'
import { changeText, rateWithCount } from '../../../lib/deskFormat'
import { fmtDate, fmtInt } from '../../../lib/format'

const sel = 'min-h-[44px] md:min-h-[32px] max-w-full bg-ink-900 border border-ink-500 px-2 font-mono text-[12px] text-paper'
const TONE = { up: 'text-radar-cyan', down: 'text-amber-glow', flat: 'text-paper-faint' } as const
const PICKUP_EXPLAIN = 'The jump right after the email line shows how much the announcement drives reading. A flatter start means the email or the cover didn’t land.'
const DEPTH_EXPLAIN = 'Share of openers who reached each part of the issue.'
const SPREADS_EXPLAIN = 'Tells editors which features and departments readers actually stop on.'

const released = (iso: string) => fmtDate(`${iso}T12:00:00Z`, { month: 'short', day: 'numeric', timeZone: 'UTC' })
const pct = (v: number) => `${Math.round(v * 1000) / 10}%`
const dayWord = (n: number) => `${n} day${n === 1 ? '' : 's'}`

/** `visitors/desk/:pub/issues` (current issue) and `.../issues/:issueId`. */
export function DeskIssues() {
  return <DeskFrame tab="issues"><IssuesBody /></DeskFrame>
}

export function IssuesBody() {
  const { pub, q, spKey } = useDeskState('issues')
  const { issueId } = useParams()
  const navigate = useNavigate()
  const p = pub as DeskPub
  const sp = new URLSearchParams(spKey)
  const allOnly = sp.get('issue') === 'all' && !issueId
  const list = useQuery({
    queryKey: ['desk', 'issues', pub, q.period, q.from, q.to, q.compare, q.group],
    queryFn: () => deskApi.issues(p, q), retry: false, placeholderData: (prev) => prev,
  })
  const d = list.data
  const single = pub !== 'all'
  const issues: IssueListItem[] = d && d.unavailable === undefined ? d.issues : []
  const id = issueId ? Number(issueId) : single && !allOnly ? issues[0]?.issue_id : undefined

  if (list.isError) return <Failed what="the issues" />
  if (!d) return <Loading />
  if (d.unavailable !== undefined) return <NotYetLive text={d.unavailable} />

  const rest = new URLSearchParams(spKey)
  rest.delete('issue')
  const go = (v: string) => {
    if (v === 'all') {
      const n = new URLSearchParams(rest)
      n.set('issue', 'all')
      navigate(`/visitors/desk/${pub}/issues?${n.toString()}`)
      return
    }
    const it = issues.find((i) => `${i.publication}:${i.issue_id}` === v)
    if (it) navigate(issueHref(it, rest.toString()))
  }
  const current = allOnly || id === undefined ? 'all'
    : (() => { const it = issues.find((i) => i.issue_id === id && (!single || i.publication === pub)); return it ? `${it.publication}:${it.issue_id}` : 'all' })()

  return (
    <div className="space-y-4">
      <GroupNote applied={d.group_applied} text={NOTE_ISSUES} />
      <Panel label="Issue" accent="amber">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label className="flex min-w-0 flex-col gap-1">
            <span className="label">Issue</span>
            <select aria-label="Issue" className={sel} value={current} onChange={(e) => go(e.target.value)}>
              {issues.map((i, k) => (
                <option key={`${i.publication}:${i.issue_id}`} value={`${i.publication}:${i.issue_id}`}>
                  {pub === 'all' ? `${i.publication_name} · ` : ''}{i.title} (released {released(i.released)}){single && k === 0 ? ' · current' : ''}
                </option>
              ))}
              <option value="all">All back issues</option>
            </select>
          </label>
          <span className="border border-ink-500 px-2 py-1 font-mono text-[11px] text-paper-dim">Members only · web viewer + PDF</span>
        </div>
        {pub === 'all' && <p className="mt-2 text-[12px] text-paper-faint">Pick one issue to see its pickup, depth and spreads. They are shown one title at a time.</p>}
      </Panel>
      {id !== undefined && single && <Detail p={p} id={id} keyBase={[pub, q.period, q.from, q.to, q.compare, q.group]} q={q} />}
      {issueId && !single && (
        <Panel accent="red"><p className="text-paper">Pick a single title to see one issue.</p></Panel>
      )}
      {issues.length === 0 && single && <Panel accent="none"><p className="text-paper-dim">No issues yet</p></Panel>}
      <AllIssues d={d} pub={pub} rest={rest.toString()} />
    </div>
  )
}

type Q = ReturnType<typeof useDeskState>['q']

function Detail({ p, id, keyBase, q }: { p: DeskPub; id: number; keyBase: unknown[]; q: Q }) {
  const query = useQuery({
    queryKey: ['desk', 'issue', ...keyBase, id], queryFn: () => deskApi.issue(p, q, id),
    retry: false, enabled: Number.isInteger(id) && id > 0, placeholderData: (prev) => prev,
  })
  if (query.isError) {
    const status = query.error instanceof ApiError ? query.error.status : 0
    return <Panel accent="red"><p className="text-paper">{status === 404 ? 'This issue is not on this title.' : status === 422 ? 'Pick a single title to see one issue.' : 'Couldn’t load the issue.'}</p></Panel>
  }
  const d = query.data
  if (!d) return <Loading />
  if (d.unavailable !== undefined) return <NotYetLive text={d.unavailable} />
  const t = d.tiles
  const prev = t.previous_issue?.title ?? null
  return (
    <>
      <header>
        <h2 className="font-serif text-[24px] leading-tight text-paper">{t.title}</h2>
        <p className="font-mono text-[12px] text-paper-faint">Released {released(t.released)} · {dayWord(t.day)} in · {fmtInt(t.members)} members</p>
      </header>
      <Tiles t={t} prev={prev} />
      <Pickup d={d} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Depth d={d} />
        <Spreads d={d} />
      </div>
    </>
  )
}

function Tile({ name, big, c, prev, sub }: { name: string; big: string; c: Compared; prev: string | null; sub: string }) {
  const tone = changeText(c).tone
  return (
    <section className="min-w-0 border border-ink-500 bg-ink-800 p-4">
      <h3 className="label">{name}</h3>
      <div className="mt-1 font-mono text-[28px] font-semibold leading-tight text-paper" style={mono}>{big}</div>
      <div className="mt-1.5 font-mono text-[13px]" style={mono}><span className={TONE[tone]}>{tileCompareText(c, prev)}</span></div>
      <div className="mt-1.5 text-[12px] text-paper-faint">{sub}</div>
    </section>
  )
}

function Tiles({ t, prev }: { t: IssueDetail['tiles']; prev: string | null }) {
  const num = (c: Compared) => (c.value === null ? '—' : fmtInt(Math.round(c.value)))
  const share = (c: Compared) => (c.value === null ? '—' : `${Math.round(c.value * 100)}%`)
  const o = t.opened_members
  const dev = t.device
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Tile name="Members who opened it" big={num(o.value)} c={o.value} prev={prev}
            sub={`${share(o.share)} of ${fmtInt(t.members)} members, ${dayWord(t.day)} in`} />
      <Tile name="PDF downloads" big={num(t.downloads)} c={t.downloads} prev={prev} sub="in the same days since release" />
      <Tile name="Median pages read" big={num(t.median_pages)} c={t.median_pages} prev={prev} sub={t.pages_total ? `of ${fmtInt(t.pages_total)}` : 'page count unknown'} />
      <Tile name="Read on a phone" big={share(dev.phone)} c={dev.phone} prev={prev}
            sub={`iPad ${share(dev.tablet)}, desktop ${share(dev.desktop)} · ${fmtInt(dev.base)} openers`} />
    </div>
  )
}

function Pickup({ d }: { d: IssueDetail }) {
  const pk = d.pickup
  return (
    <Panel label="Pickup · share of members who have opened the issue, by days since release" accent="cyan">
      {!pk ? <p className="text-paper-dim">No pickup curve yet</p> : <PickupChart pk={pk} />}
      <p className="mt-2 text-[12px] text-paper-faint">{PICKUP_EXPLAIN}</p>
    </Panel>
  )
}

function PickupChart({ pk }: { pk: NonNullable<IssueDetail['pickup']> }) {
  const { series, labels, markers } = pickupSeries(pk)
  const cur = pk.series[0]
  const last = cur ? [...cur.points].map((v, i) => ({ v, i })).filter((x) => x.v !== null).pop() : undefined
  const aria = last && cur ? `Pickup curve: ${cur.title} reached ${pct(last.v as number)} of members by day ${last.i}` : 'Pickup curve'
  return (
    <div className="space-y-2">
      <TrendChart series={series} labels={labels} markers={markers} ariaLabel={aria} format={pct} />
      {pk.email_before_release && <p className="text-[12px] text-paper-faint">The new-issue email went out before the release day; it is drawn at day 0.</p>}
      {pk.email_sent_day === null && <p className="text-[12px] text-paper-faint">No new-issue email has been sent for this issue.</p>}
    </div>
  )
}

function Depth({ d }: { d: IssueDetail }) {
  const bars = depthBars(d.depth, d.tiles.pages_total)
  const allNull = bars.every((b) => b.pct === null)
  return (
    <Panel label="How far into the issue readers get" accent="green">
      {bars.length === 0 ? <p className="text-paper-dim">Page count unknown for this issue</p> : allNull ? (
        <p className="text-paper-dim">Fewer than 30 people opened this issue ({fmtInt(d.depth.openers)}), so no percentages yet</p>
      ) : (
        <div>
          <div className="flex h-[150px] items-end gap-1 border-b border-ink-500" role="img"
               aria-label={`Share of openers reaching each tenth of the issue: ${bars.map((b) => `${b.label} ${b.pct === null ? 'unknown' : `${b.pct}%`}`).join(', ')}`}>
            {bars.map((b) => (
              <span key={b.label} title={`${b.label}: ${b.pct === null ? '—' : `${b.pct}%`}`} className="min-w-[8px] flex-1 bg-radar-cyan"
                    style={{ height: `${b.pct ?? 0}%` }} />
            ))}
          </div>
          <div className="flex justify-between font-mono text-[11px] text-paper-faint">
            <span>{bars[0].label}</span><span>{bars[Math.floor(bars.length / 2) - 1].label}</span><span>{bars[bars.length - 1].label}</span>
          </div>
          <details className="mt-2 font-mono text-[11px] text-paper-dim">
            <summary className="cursor-pointer text-paper-faint">Show the numbers</summary>
            <ul className="mt-1">{bars.map((b) => <li key={b.label}>{b.label}: {b.pct === null ? '—' : `${b.pct}%`}</li>)}</ul>
          </details>
        </div>
      )}
      <p className="mt-2 text-[12px] text-paper-faint">{DEPTH_EXPLAIN} Based on {fmtInt(d.depth.openers)} openers.</p>
    </Panel>
  )
}

function Spreads({ d }: { d: IssueDetail }) {
  return (
    <Panel label="Most-read spreads" accent="violet">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-[13px]">
          <thead><tr><th className={th}>Pages</th><th className={th}>What’s there</th><th className={`${th} text-right`}>Readers</th><th className={`${th} text-right`}>Median time</th></tr></thead>
          <tbody>
            {d.spreads.map((s) => (
              <tr key={s.pages} className="border-t border-ink-600">
                <td className={`${td} font-mono`} style={mono}>{s.pages}</td>
                <td className={td}><span className="block max-w-[260px] truncate" title={s.what}>{s.what || '—'}</span></td>
                <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(s.readers)}</td>
                <td className={`${td} text-right font-mono`} style={mono}>{mmss(s.median_time_ms)}</td>
              </tr>
            ))}
            {d.spreads.length === 0 && <tr><td colSpan={4} className={`${td} text-paper-faint`}>No spread reads yet</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[12px] text-paper-faint">{SPREADS_EXPLAIN}</p>
    </Panel>
  )
}

type Ok = IssuesResp & { unavailable?: undefined }

function AllIssues({ d, pub, rest }: { d: Ok; pub: string; rest: string }) {
  const a = d.all
  return (
    <Panel label={d.heading || 'All issues'} accent="amber">
      <div className="space-y-2">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-[13px]">
            <thead>
              <tr>
                <th className={th}>Issue</th><th className={`${th} text-right`}>Members reached</th><th className={`${th} text-right`}>Opened in viewer</th>
                <th className={`${th} text-right`}>PDF downloads</th><th className={`${th} text-right`}>Median pages read</th>
                <th className={`${th} text-right`}>Days to reach 15%</th><th className={`${th} text-right`}>Opens this period</th>
              </tr>
            </thead>
            <tbody>
              {a.rows.map((r) => <Row key={`${r.publication}:${r.issue_id}`} r={r} pub={pub} rest={rest} />)}
              {a.rows.length === 0 && <tr><td colSpan={7} className={`${td} text-paper-faint`}>No issues yet</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[12px] text-paper-faint">
          Back issues were {rateWithCount(a.back_issue_share, a.back_issue_opens)} of all {fmtInt(a.period_opens)} issue opens this period.
          {a.back_issue_share === null && ' (Under 30 opens, so no percentage.)'} {d.back_note}
        </p>
      </div>
    </Panel>
  )
}

function Row({ r, pub, rest }: { r: IssueRow; pub: string; rest: string }) {
  const dest = issueHref(r, rest)
  return (
    <tr className="border-t border-ink-600">
      <td className={td}>
        <Link to={dest} title={r.title} className="block max-w-[260px] truncate font-semibold text-amber-glow hover:underline">{r.title}</Link>
        <div className="text-[12px] text-paper-faint">{pub === 'all' ? `${r.publication_name} · ` : ''}released {released(r.released)}</div>
      </td>
      <td className={`${td} text-right font-mono`} style={mono}>{r.members_reached_share === null ? '—' : `${Math.round(r.members_reached_share * 100)}%`}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.opened)}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.downloads)}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{r.median_pages === null ? '—' : fmtInt(Math.round(r.median_pages))}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{r.days_to_15pct === null || r.days_to_15pct === undefined ? '—' : r.days_to_15pct}</td>
      <td className={`${td} text-right font-mono`} style={mono}>{fmtInt(r.opens_in_period)}</td>
    </tr>
  )
}
